const popupStack = [];
const previouslyFocusedElements = new Map();

/**
 * @type {boolean} Flag to ignore focus event
 */
let ignoreFocusEvent = false;

/**
 * @type {Element}
 */
let previouslyFocusedElement = null;

/**
 * Creates fixed invisible node to wrap the popup to ensure that focus is kept within the popup
 * @param post {boolean} Whether to create pre or post node
 * @returns {HTMLDivElement}
 */
function createNode (post = false) {
    const node = document.createElement('div');
    node.style.position = 'fixed';
    node.style.left = '0';
    node.style[post ? 'bottom' : 'top'] = '0';
    node.style.width = '0';
    node.style.height = '0';
    node.tabIndex = 0;
    return node;
}

const preNode = createNode();
const postNode = createNode(true);

/**
 * Moves pre and post nodes around the popup element
 * @param element - popup element
 */
function moveNodes (element) {
    element.parentNode.insertBefore(preNode, element);
    element.parentNode.insertBefore(postNode, element.nextSibling);
}

function tryFocus (node) {
    if (typeof node !== 'object' || typeof node.focus !== 'function') return false;

    try {
        ignoreFocusEvent = true;
        node.focus();
    } catch (e) {
    } finally {
        ignoreFocusEvent = false;
    }

    return document.activeElement === node;
}

function focusFirst(node) {
    if (tryFocus(node)) return true;
    for (let i = 0; i < node.children.length; i++) {
        if (focusFirst(node.children[i])) return true;
    }
    return false;
}

function focusLast (node) {
    if (tryFocus(node)) return true;
    for (let i = node.children.length - 1; i >= 0; i--) {
        if (focusLast(node.children[i])) return true;
    }
    return false;
}

function onFocus () {
    if (ignoreFocusEvent || popupStack.length === 0) return;

    const popupNode = popupStack[popupStack.length - 1];
    if (popupNode.contains(document.activeElement)) return;

    focusFirst(popupNode)
    if (document.activeElement === previouslyFocusedElement) focusLast(popupNode);

    previouslyFocusedElement = document.activeElement;
}

function registerListener () {
    document.addEventListener('focus', onFocus, true);
}

function unregisterListener () {
    document.removeEventListener('focus', onFocus, true);
}

/**
 * when opening a popup/modal this ensures that focus will not leave the area
 * @param node - element to lock popup too
 * @param focusNode - which element to focus instead of first child
 */
function lock(node, focusNode) {
    if (popupStack.length === 0) {
        registerListener();
        document.documentElement.classList.add('has-popup');
    }
    previouslyFocusedElements.set(node, document.activeElement);
    popupStack.push(node);
    moveNodes(node);
    if (focusNode) tryFocus(focusNode);
    else focusFirst(node);
}

/**
 * when closing modal this removes focus locking to the element
 * @param node {HTMLElement} - popup element (needs to be the same that the lock method was called on)
 */
function release(node) {
    const index = popupStack.indexOf(node)
    if (index !== -1) popupStack.splice(index, 1);
    if (popupStack.length === 0) {
        unregisterListener();
        document.documentElement.classList.remove('has-popup');
    }
    tryFocus(previouslyFocusedElements.get(node));
}

const Popup = {
    lock: lock,
    release: release,
}

export default Popup;
